import type { SubPageContent } from "@/components/common/SubPageLayout";
import type { Lang } from "@/lib/i18n";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "PetrochemicalProcessFiltration",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Oil, Fuel & Petrochemical Filtration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Stainless Steel Mesh & Sintered Elements for Petrochemical Process Filtration",
      lead: "Engineered woven wire meshes and diffusion-bonded sintered multilayer laminates built to protect cracking catalyst beds, reforming compressors, and sensitive instrumentation across severe refining and petrochemical process streams.",
      secondaryCta: {
        label: "Oil & Fuel Filtration",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Industrial high-pressure filtration vessel and elements in a petrochemical processing facility",
      },
      cardNote: "All-metal construction & high collapse strength",
      cardLink: {
        label: "Request Technical Review",
        to: "/ContactPage",
      },
      specs: [
        { label: "Pore Rating", value: "1 µm – 250 µm" },
        { label: "Standard Alloys", value: "AISI 316L / Inconel / C-276" },
        { label: "Media Construction", value: "Sintered Laminate / Wire Mesh" },
        { label: "Operating Environment", value: "Severe Chemical & Thermal Duty" },
      ],
    },
    overview: {
      eyebrow: "Process Engineering",
      heading: "Severe-Service Filtration for Catalytic Refining and Chemical Synthesis",
      paragraphs: [
        "Petrochemical refining, hydrocracking, catalytic reforming, and polymer synthesis units operate continuously under severe conditions where process fluids carry catalyst fines, corrosive chemical species, and aggressive gas mixtures. In these high-temperature, high-differential-pressure circuits, polymer or fibrous filter media are unworkable due to thermal degradation, chemical dissolution, or media migration.",
        "MutualFilter supplies precision-engineered all-metal filtration media, including diffusion-bonded multilayer sintered mesh laminates and heavy calendered woven wire cloth. The rigid porous matrix maintains fixed pore geometry under turbulent fluid surges and alternating flow directions, preventing catalyst fines carryover and protecting downstream compressors, valves, and heat exchanger bundles.",
        "Fabricated in corrosion-resistant metallurgy including passivated AISI 316L, Inconel 625, and Hastelloy C-276, our petrochemical filter components resist chloride stress cracking, wet sour gas exposure, and caustic regeneration washdowns without loss of structural integrity.",
      ],
      keyPoints: [
        "Rigid diffusion-bonded porous composites preserve micron-rated pore integrity under severe differential pressure conditions",
        "Thermal cycling stability maintained across reaction and regeneration zones without wire distortion or media shedding",
        "Full alloy traceability with 316L, Inconel 625, and Hastelloy C-276 for wet sour gas and chloride acid resistance",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "High-pressure filtration vessel and elements in a petrochemical facility",
      },
    },
    specTable: {
      heading: "Petrochemical Filtration Media Configurations",
      intro: "Representative metallic filter media configurations tailored for catalyst recovery, gas scrubbing, amine loops, and reactor bed support.",
      columns: [
        { key: "process", label: "Process Duty", align: "left" },
        { key: "construction", label: "Filter Media Construction", align: "left" },
        { key: "rating", label: "Filtration Rating", align: "right" },
        { key: "alloys", label: "Standard Alloys", align: "right" },
        { key: "cleaning", label: "Regeneration Method", align: "right" },
      ],
      rows: [
        {
          process: "Hydrocracking Catalyst Retention",
          construction: "5-layer sintered mesh composite",
          rating: "2 µm – 40 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Automated blowback / ultrasonic",
        },
        {
          process: "Reforming Compressor Gas Protection",
          construction: "Pleated twill Dutch weave cylinder",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L / Hastelloy C-276",
          cleaning: "Chemical wash / backflush",
        },
        {
          process: "Amine Sweetening & Glycol Reboilers",
          construction: "Sintered laminate cylindrical cartridge",
          rating: "5 µm – 75 µm",
          alloys: "AISI 316L",
          cleaning: "Solvent flush / steam purge",
        },
        {
          process: "Reactor Bed Support & Underdrain",
          construction: "Continuous-slot wedge wire screen panel",
          rating: "100 µm – 250 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Mechanical sweep / high-pressure wash",
        },
      ],
      caption: "All aperture ratings, layer counts, and structural dimensions are engineered to order to match specific vessel designs and process chemistries.",
    },
    features: {
      heading: "Key Operational & Manufacturing Advantages",
      intro: "Our stainless steel and high-nickel petrochemical filter media combine high collapse strength, thermal stability, and tight aperture uniformity across continuous severe service.",
      items: [
        {
          title: "Zero Media Migration",
          text: "Solid-state diffusion bonding fuses contact points between mesh layers, eliminating loose fibers, wire displacement, and particulate shedding into high-value chemical streams.",
        },
        {
          title: "Thermal & Structural Rigidity",
          text: "Heavy outer guard layers and reinforced perforated inner cores deliver high collapse strength during process transients, rapid pressure swings, and temperature cycles.",
        },
        {
          title: "Severe Chemistry Compatibility",
          text: "Manufactured from passivated 316L stainless steel, Inconel 625, or Hastelloy C-276 to prevent pitting and stress corrosion cracking in sour, chloride-laden, and acidic environments.",
        },
        {
          title: "Repeatable Cleanability",
          text: "All-welded metallic construction supports in-situ gas pulse blowback, solvent recirculating washes, steam purging, and offline ultrasonic bath regeneration for long service life.",
        },
      ],
    },
    related: {
      heading: "Related Oil & Fuel Filtration Applications and Products",
      intro: "Explore complementary liquid and gas separation solutions and metallic filter media manufactured for demanding energy and chemical processing facilities.",
      cards: [
        {
          title: "Oil & Fuel Filtration",
          text: "High-pressure fuel lines, hydraulic systems, lube circuits, and refinery separation solutions built with stainless steel filter media.",
          to: "/OilFuelFiltrationPage",
          badge: "Parent Application",
          linkLabel: "View Application Overview",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Oil, fuel and petrochemical filtration overview showing stainless steel filter element",
          },
        },
        {
          title: "Oil and Gas Separation",
          text: "Downhole sand control, gas scrubber mist elimination, and produced water separation engineered for high mechanical collapse resistance.",
          to: "/OilGasSeparationPage",
          badge: "Sibling Application",
          linkLabel: "Explore Oil & Gas Separation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Stainless steel multi-layer mesh screen for oil and gas solid-liquid and gas-liquid separation",
          },
        },
        {
          title: "Oil Fuel Filtration Applications",
          text: "Heavy-duty woven and sintered media for aviation fuel filtration, hydraulic circuits, turbine lube conditioning, and marine heavy fuel oil.",
          to: "/OilFuelFiltrationApplicationsPage",
          badge: "Sibling Application",
          linkLabel: "Explore Fuel & Hydraulic",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
            alt: "Stainless steel fuel and hydraulic fluid filtration mesh element detail",
          },
        },
        {
          title: "Sintered Wire Mesh",
          text: "Diffusion-bonded multilayer mesh laminates offering rigid pore geometry, high differential pressure resistance, and zero media migration.",
          to: "/SinteredWireMeshPage",
          badge: "Recommended Product",
          linkLabel: "Explore Sintered Mesh",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Multilayer sintered wire mesh laminate disc for hydraulic and oil filtration",
          },
        },
        {
          title: "Filter Components",
          text: "All-welded cylindrical and pleated filter cartridges, elements, and strainer baskets for high-pressure industrial fluid processing.",
          to: "/FilterComponentsPage",
          badge: "Recommended Product",
          linkLabel: "Explore Filter Components",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Stainless steel pleated and cylindrical filter cartridges for oil and fuel service",
          },
        },
      ],
    },
    cta: {
      id: "petrochemical-quote",
      eyebrow: "Engineering Consultation",
      heading: "Request a Custom Petrochemical Filter Specification",
      lead: "Provide your required micron rating, process chemistry, operating conditions, and element geometry for a technical evaluation and formal quotation.",
      secondaryLabel: "Oil & Fuel Filtration",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Material test reports (MTR) and positive material identification (PMI) available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "PetrochemicalProcessFiltration",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "油品、燃料与石化过滤",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "石化工艺过程过滤不锈钢丝网与烧结滤材",
      lead: "专为加氢裂解催化剂截留、重整压缩机保护及高腐蚀工艺介质设计的精密编织丝网与扩散焊接多层烧结复合滤材，在高温交变应力与严苛化学工况下提供可靠固体拦截。",
      secondaryCta: {
        label: "油品与燃料过滤总览",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "石化设施高压过滤容器与耐高温耐腐蚀工业滤网元件",
      },
      cardNote: "全金属刚性结构与抗压差失稳设计",
      cardLink: {
        label: "技术方案咨询",
        to: "/ContactPage",
      },
      specs: [
        { label: "过滤精度范围", value: "1 µm – 250 µm" },
        { label: "常用合金材质", value: "AISI 316L / Inconel / C-276" },
        { label: "滤材结构形式", value: "多层烧结板 / 精密编织网" },
        { label: "工况适用性", value: "高温高压与强腐蚀工艺介质" },
      ],
    },
    overview: {
      eyebrow: "工艺工程解析",
      heading: "石化炼化反应与催化剂截留的高可靠严苛工况过滤",
      paragraphs: [
        "石化炼油、重整加氢、乙烯裂解及有机合成装置常年处于严苛高温高压与多相介质共存的复杂环境。工艺流体中常夹带细微催化剂粉末、酸性腐蚀介质及聚合胶质，聚合物与纤维滤材极易在高温腐蚀下发生熔毁、降解与介质脱落，无法胜任严苛工况。",
        "MutualFilter 提供工程级全金属过滤介质，包括真空扩散焊接多层复合烧结金属网板及高密度精轧编织丝网。固态熔合的多层刚性孔径结构在剧烈压差脉动与反向流体冲击下保持几何微孔恒定，杜绝微细催化剂颗粒穿透，保护下游压缩机叶轮、精密阀门与热交换器管束。",
        "元件精选经酸洗钝化的 AISI 316L 不锈钢、因科镍 Inconel 625 与哈氏合金 Hastelloy C-276 精工制造，全面抵抗湿硫化氢、含氯酸性气体与强碱性化学清洗介质侵蚀，具备长周期的机械抗蠕变稳定性。",
      ],
      keyPoints: [
        "高刚性扩散焊接多层复合结构，在承受瞬时脉动与反洗压差时不发生网孔失稳",
        "优异的高温热稳定性，耐受连续热裂解与催化剂再生工况下的温度交变冲击",
        "提供 316L、因科镍 625 与哈氏合金 C-276 等耐蚀合金，全面抵御湿硫化氢与氯离子点蚀",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "石化设施高压过滤容器与耐高温耐腐蚀工业滤网元件",
      },
    },
    specTable: {
      heading: "石化工艺过滤介质典型配置参数",
      intro: "以下列出适用于加氢裂解、重整气体净化、胺液回路及反应器床层支撑的典型金属过滤构型。",
      columns: [
        { key: "process", label: "工艺应用环节", align: "left" },
        { key: "construction", label: "核心滤材构型", align: "left" },
        { key: "rating", label: "名义过滤精度", align: "right" },
        { key: "alloys", label: "推荐合金材质", align: "right" },
        { key: "cleaning", label: "再生清洗方式", align: "right" },
      ],
      rows: [
        {
          process: "加氢裂解催化剂粉末拦截",
          construction: "5层扩散焊接复合烧结网",
          rating: "2 µm – 40 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "自动气体反吹 / 超声波清洗",
        },
        {
          process: "重整压缩机进气微尘保护",
          construction: "折叠式斜纹荷兰编织滤筒",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L / Hastelloy C-276",
          cleaning: "溶剂反洗 / 化学药剂冲洗",
        },
        {
          process: "脱硫胺液循环与醇类重沸器",
          construction: "烧结复合多孔圆柱滤芯",
          rating: "5 µm – 75 µm",
          alloys: "AISI 316L",
          cleaning: "蒸汽吹扫 / 溶剂循环冲洗",
        },
        {
          process: "反应器床层支撑与底部集液",
          construction: "连续缝隙楔形丝筛板",
          rating: "100 µm – 250 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "高压水射流 / 机械清理",
        },
      ],
      caption: "各项截留精度、层数配置与外形连接形式均可根据特定容器结构及流体工艺要求定制生产。",
    },
    features: {
      heading: "关键运行与结构技术优势",
      intro: "我们的不锈钢与高镍合金石化过滤介质兼具抗压差崩塌刚性、热交变稳定性与孔径均一性，适应长周期严苛运行。",
      items: [
        {
          title: "零介质脱落安全性",
          text: "真空固相扩散焊接工艺将各层金属丝交差点牢固熔结，杜绝纤维碎屑剥落与丝径位移，保障高价值化学工艺介质不受二次污染。",
        },
        {
          title: "高抗压差机械刚性",
          text: "采用厚重冲孔骨架与高强度保护外护套，在面对工艺瞬态冲击、反吹脉冲与高压差工况时维持结构完整不变形。",
        },
        {
          title: "耐受严苛酸性腐蚀",
          text: "选用酸洗钝化 316L、Inconel 625 与 Hastelloy C-276 耐蚀合金制造，有效防止酸性含硫气液及氯化物环境下的应力腐蚀开裂与点蚀。",
        },
        {
          title: "多样化原位与离线再生",
          text: "全金属熔焊构件耐受在线气体反吹脉冲、溶剂回流清洗、高温蒸汽吹扫以及离线超声波清洗，实现滤芯长期重复使用。",
        },
      ],
    },
    related: {
      heading: "相关油气与石化过滤应用及推荐产品",
      intro: "浏览 MutualFilter 为能源化工领域打造的互补工艺过滤方案与工程级金属过滤介质。",
      cards: [
        {
          title: "油品、燃料与石化过滤",
          text: "涵盖高压燃油管路、液压系统、润滑回路与石化炼化分离的全金属滤网与滤芯综合解决方案。",
          to: "/OilFuelFiltrationPage",
          badge: "父级应用分类",
          linkLabel: "查看应用总览",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "油品、燃料与石化过滤总览及不锈钢过滤元件",
          },
        },
        {
          title: "油气分离与防砂净化",
          text: "专为井口防砂、天然气脱硫除沫及采出水深度分离设计的高机械强度金属丝网与多层复合构件。",
          to: "/OilGasSeparationPage",
          badge: "同级应用",
          linkLabel: "探索油气分离",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "油气固液与气液多相分离多层不锈钢丝网滤板",
          },
        },
        {
          title: "燃油与液压流体应用",
          text: "用于航空燃油净化、高压液压控制回路及重油净化的重载金属编织与烧结复合滤材。",
          to: "/OilFuelFiltrationApplicationsPage",
          badge: "同级应用",
          linkLabel: "探索燃油与液压",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
            alt: "不锈钢燃油与液压流体过滤精密滤芯微观结构",
          },
        },
        {
          title: "多层金属烧结网",
          text: "扩散焊接多层复合金属丝网，具备刚性孔径几何、卓越抗压差失稳强度及零介质脱落特性。",
          to: "/SinteredWireMeshPage",
          badge: "推荐产品",
          linkLabel: "探索烧结网",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "用于液压与油品过滤的多层烧结金属网滤盘",
          },
        },
        {
          title: "过滤组件与滤芯",
          text: "全焊接圆柱与折叠滤芯、不锈钢滤筒与精密滤篮，满足高压工况下流体杂质高效截留需求。",
          to: "/FilterComponentsPage",
          badge: "推荐产品",
          linkLabel: "探索过滤组件",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "油品与燃油用不锈钢折叠及圆柱滤芯",
          },
        },
      ],
    },
    cta: {
      id: "petrochemical-quote",
      eyebrow: "定制方案咨询",
      heading: "获取石化工艺过滤滤芯与金属滤网技术报价",
      lead: "请提供您的截留精度、工艺介质特性、运行工况及滤芯机械安装尺寸，我们的工程师将为您提供技术评估与选型报价。",
      secondaryLabel: "返回油品与燃料过滤",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "材质检验证明 (MTR) 与 PMI 化学成分分析可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "PetrochemicalProcessFiltration",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Filtración de Aceites, Combustibles y Petroquímica",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Mallas y Elementos Sinterizados de Acero Inoxidable para Procesos Petroquímicos",
      lead: "Mallas de alambre tejidas y laminados multicapa sinterizados por difusión diseñados para proteger lechos de catalizadores, compresores de reformado e instrumentación en corrientes petroquímicas agresivas.",
      secondaryCta: {
        label: "Filtración de Aceites y Combustibles",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Recipiente de filtración industrial de alta presión en planta petroquímica",
      },
      cardNote: "Construcción metálica rígida y alta resistencia al colapso",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Rango de Micras", value: "1 µm – 250 µm" },
        { label: "Aleaciones Estándar", value: "AISI 316L / Inconel / C-276" },
        { label: "Medio Filtrante", value: "Laminado Sinterizado / Malla Tejida" },
        { label: "Ambiente Operativo", value: "Servicio Químico y Térmico Severo" },
      ],
    },
    overview: {
      eyebrow: "Ingeniería de Procesos",
      heading: "Filtración en Servicio Severo para Refino Catalítico y Síntesis Química",
      paragraphs: [
        "Las unidades de refino petroquímico, hidrocarburos, reformado catalítico y síntesis química operan en condiciones exigentes donde los fluidos del proceso arrastran finos de catalizador, compuestos químicos corrosivos y gases ácidos. En estos circuitos de alta temperatura y elevada presión diferencial, los medios poliméricos o fibrosos fallan por degradación térmica o desprendimiento.",
        "MutualFilter suministra medios filtrantes totalmente metálicos diseñados para ingeniería de procesos, incluyendo laminados sinterizados multicapa unidos por difusión y mallas de alambre de alta resistencia. La estructura porosa rígida mantiene una apertura exacta bajo pulsos de presión y cambios de caudal, evitando el arrastre de catalizador hacia compresores y cambiadores de calor.",
        "Fabricados en aleaciones resistentes a la corrosión como AISI 316L pasivado, Inconel 625 y Hastelloy C-276, los elementos soportan ambientes con gas amargo húmedo, cloruros y limpiezas cáusticas sin pérdida de integridad estructural.",
      ],
      keyPoints: [
        "Compuestos porosos rígidos unidos por difusión que conservan la integridad del poro bajo presiones diferenciales exigentes",
        "Estabilidad térmica sostenida en zonas de reacción y regeneración sin distorsión de la malla ni desprendimiento",
        "Aleaciones 316L, Inconel 625 y Hastelloy C-276 que evitan agrietamiento por corrosión bajo tensión en gas amargo y cloruros",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Recipiente de filtración industrial en instalación petroquímica",
      },
    },
    specTable: {
      heading: "Configuraciones de Medios Filtrantes para Procesos Petroquímicos",
      intro: "Medios filtrantes metálicos representativos para retención de catalizador, acondicionamiento de gas y soporte de lecho de reactores.",
      columns: [
        { key: "process", label: "Aplicación de Proceso", align: "left" },
        { key: "construction", label: "Construcción del Medio", align: "left" },
        { key: "rating", label: "Grado de Filtración", align: "right" },
        { key: "alloys", label: "Aleaciones Típicas", align: "right" },
        { key: "cleaning", label: "Método de Regeneración", align: "right" },
      ],
      rows: [
        {
          process: "Retención de Catalizador en Hidrocraqueo",
          construction: "Compuesto de malla sinterizada de 5 capas",
          rating: "2 µm – 40 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Contracorriente por gas / ultrasonidos",
        },
        {
          process: "Protección de Compresores de Reformado",
          construction: "Cartucho plisado de tejido Dutch cruzado",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L / Hastelloy C-276",
          cleaning: "Lavado con solvente / retrolavado",
        },
        {
          process: "Circuitos de Amina y Rebouilleurs de Glicol",
          construction: "Cilindro poroso laminado sinterizado",
          rating: "5 µm – 75 µm",
          alloys: "AISI 316L",
          cleaning: "Purgado con vapor / recirculación",
        },
        {
          process: "Soporte de Lecho en Reactores",
          construction: "Panel plano de alambre en cuña",
          rating: "100 µm – 250 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Lavado a presión / barrido mecánico",
        },
      ],
      caption: "Todos los rangos de micras, dimensiones y conexiones se fabrican a medida según las especificaciones del cliente.",
    },
    features: {
      heading: "Ventajas Operativas y de Fabricación",
      intro: "Nuestros medios filtrantes para petroquímica aúnan alta resistencia mecánica al colapso, estabilidad térmica y precisión de corte micrométrico.",
      items: [
        {
          title: "Cero Migración de Fibras",
          text: "La unión por difusión en fase sólida suelda los alambres en cada punto de contacto, impidiendo desprendimientos en corrientes de hidrocarburos de alto valor.",
        },
        {
          title: "Rigidez Estructural y Térmica",
          text: "Mallas de soporte pesadas y tubos perforados interiores soportan sobrepresiones y ciclos térmicos intensos sin deformación.",
        },
        {
          title: "Compatibilidad Química Severa",
          text: "Fabricación en aceros AISI 316L pasivados e Inconel 625 que toleran efluentes amargos, compuestos sulfurados y soluciones ácidas.",
        },
        {
          title: "Regeneración Confiable",
          text: "Estructuras totalmente soldadas aptas para impulsos de soplado con gas, lavados químicos y limpieza por ultrasonidos fuera de línea.",
        },
      ],
    },
    related: {
      heading: "Aplicaciones y Productos Relacionados",
      intro: "Explore soluciones complementarias en filtración de hidrocarburos y medios metálicos diseñados para la industria petroquímica.",
      cards: [
        {
          title: "Filtración de Aceites y Combustibles",
          text: "Soluciones de separación metálica para circuitos hidráulicos, lubricación y refino en alta presión.",
          to: "/OilFuelFiltrationPage",
          badge: "Aplicación Principal",
          linkLabel: "Ver Visión General",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Visión general de filtración de aceites y combustibles",
          },
        },
        {
          title: "Separación de Petróleo y Gas",
          text: "Control de arena en pozos, eliminación de nieblas en gas y tratamiento de aguas de producción.",
          to: "/OilGasSeparationPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Petróleo y Gas",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Panel multicapa para separación de petróleo y gas",
          },
        },
        {
          title: "Aplicaciones en Combustible y Fluidos Hidráulicos",
          text: "Medios tejidos y sinterizados para queroseno de aviación, sistemas hidráulicos y fueloil pesado.",
          to: "/OilFuelFiltrationApplicationsPage",
          badge: "Aplicación Hermana",
          linkLabel: "Explorar Combustible e Hidráulica",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
            alt: "Elemento filtrante para combustible y fluidos hidráulicos",
          },
        },
        {
          title: "Malla Sinterizada Multicapa",
          text: "Laminados sinterizados con estructura rígida de poros, alta resistencia mecánica y nula migración.",
          to: "/SinteredWireMeshPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Malla Sinterizada",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco sinterizado multicapa para filtración industrial",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cartuchos cilíndricos y plisados totalmente soldados en acero inoxidable para filtración a alta presión.",
          to: "/FilterComponentsPage",
          badge: "Producto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Cartuchos filtrantes plisados y cilíndricos de acero inoxidable",
          },
        },
      ],
    },
    cta: {
      id: "petrochemical-quote",
      eyebrow: "Consulta Técnica",
      heading: "Solicite Cotización para Filtros de Procesos Petroquímicos",
      lead: "Indique el grado de retención requerido, la química del proceso, condiciones térmicas y dimensiones para una evaluación de ingeniería.",
      secondaryLabel: "Volver a Aceites y Combustibles",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Informes de ensayo de material (MTR) e identificación positiva de materiales (PMI) disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "PetrochemicalProcessFiltration",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Öl-, Kraftstoff- und Petrochemiefiltration",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Edelstahldrahtgewebe & Sinterelemente für die petrochemische Prozessfiltration",
      lead: "Präzisions-Drahtgewebe und diffusionsgeschweißte mehrlagige Sinterverbunde zum Schutz von Spaltkatalysatorbetten, Reformerkompressoren und empfindlichen Instrumenten bei aggressiven petrochemischen Prozessströmen.",
      secondaryCta: {
        label: "Öl- und Kraftstofffiltration",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Industrieller Hochdruck-Filterbehälter in einer petrochemischen Raffinerieanlage",
      },
      cardNote: "Ganzmetallkonstruktion & hohe Kollapsfestigkeit",
      cardLink: {
        label: "Technische Beratung",
        to: "/ContactPage",
      },
      specs: [
        { label: "Filterfeinheit", value: "1 µm – 250 µm" },
        { label: "Standard-Werkstoffe", value: "AISI 316L / Inconel / C-276" },
        { label: "Filtermedien", value: "Sinterverbund / Drahtgewebe" },
        { label: "Einsatzbereich", value: "Schwerer chemischer & thermischer Betrieb" },
      ],
    },
    overview: {
      eyebrow: "Verfahrenstechnik",
      heading: "Robuste Prozessfiltration für katalytische Raffination und chemische Synthese",
      paragraphs: [
        "Petrochemische Raffinerien, Hydrocracker, katalytische Reformer und Syntheseanlagen arbeiten unter anspruchsvollen Bedingungen, bei denen Prozessströme Katalysatorabrieb, korrosive Verbindungen und aggressive Gase mitführen. In diesen Hochtemperatur- und Hochdruckkreisläufen sind faserige oder polymere Medien wegen Zersetzungsgefahr ungeeignet.",
        "MutualFilter liefert präzise Ganzmetall-Filtermedien wie diffusionsgebundene Sintermetall-Laminate und schwere gewalzte Drahtgewebe. Die starre poröse Struktur widersteht Druckstößen und wechselnden Strömungsrichtungen ohne Porenveränderung, was den Katalysatorrückhalt sichert und nachgeschaltete Kompressoren sowie Wärmetauscher schützt.",
        "Aus korrosionsbeständigen Güten wie passiviertem AISI 316L, Inconel 625 und Hastelloy C-276 gefertigt, halten die Filterelemente feuchtem Sauergas, Chloriden und Laugenreinigungen dauerhaft stand.",
      ],
      keyPoints: [
        "Diffusionsgeschweißte Sinterverbunde sichern formstabile Porengeometrie bei anspruchsvollen Differenzdrücken",
        "Hohe Temperaturwechselbeständigkeit in thermischen Reaktions- und Regenerationszonen ohne Gefügeversagen",
        "Werkstoffauswahl in 316L, Inconel 625 und Hastelloy C-276 gegen Lochfraß und Spannungsrisskorrosion",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Hochdruck-Filteranlage in einer petrochemischen Anlage",
      },
    },
    specTable: {
      heading: "Filtermedien-Konfigurationen für petrochemische Prozesse",
      intro: "Repräsentative metallische Filtermedien für Katalysatorrückhaltung, Gasaufbereitung, Aminwäschen und Reaktorböden.",
      columns: [
        { key: "process", label: "Prozessaufgabe", align: "left" },
        { key: "construction", label: "Filtermedium-Aufbau", align: "left" },
        { key: "rating", label: "Nennfeinheit", align: "right" },
        { key: "alloys", label: "Standard-Werkstoffe", align: "right" },
        { key: "cleaning", label: "Reinigungsverfahren", align: "right" },
      ],
      rows: [
        {
          process: "Katalysatorrückhaltung im Hydrocracker",
          construction: "5-lagiger Sintergewebeverbund",
          rating: "2 µm – 40 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Automatisches Gasrückblasen / Ultraschall",
        },
        {
          process: "Kompressorschutz in Reformieranlagen",
          construction: "Plissierte Köpertressen-Filterkerze",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L / Hastelloy C-276",
          cleaning: "Lösemittelspülung / Rückspülung",
        },
        {
          process: "Aminwäsche & Glykol-Reboiler",
          construction: "Mehrlagiger zylindrischer Sinterfilter",
          rating: "5 µm – 75 µm",
          alloys: "AISI 316L",
          cleaning: "Dampfspülung / Lösemittelkreislauf",
        },
        {
          process: "Reaktorbett-Stützboden",
          construction: "Spaltsieb-Flachpaneel mit V-Draht",
          rating: "100 µm – 250 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Hochdruck-Sprühreinigung / mechanisch",
        },
      ],
      caption: "Alle Feinheiten, Lagenanzahlen und Einbaumaße werden auftragsbezogen an Behälter und Betriebsbedingungen angepasst.",
    },
    features: {
      heading: "Betriebliche & Konstruktive Vorteile",
      intro: "Unsere Edelstahl- und Nickellegierungsmedien vereinen hohe Kollapsfestigkeit, thermische Beständigkeit und gleichmäßige Porenöffnungen.",
      items: [
        {
          title: "Keine Medienmigration",
          text: "Diffusionsschweißen verschmilzt Drahtkreuzungspunkte fest miteinander, wodurch das Ablösen von Fasern in empfindliche Kohlenwasserstoffströme ausgeschlossen wird.",
        },
        {
          title: "Mechanische & Thermische Festigkeit",
          text: "Robuste äußere Schutzlagen und innenliegende Stützrohre halten extremen Druckdifferenzen und schnellen Temperaturwechseln stand.",
        },
        {
          title: "Beständigkeit in aggressiver Chemie",
          text: "Passiviertes 316L, Inconel 625 und Hastelloy C-276 verhindern Spannungsrisskorrosion und Lochfraß in schwefel- und chloridhaltigen Gasen.",
        },
        {
          title: "Verlässliche Regenerierbarkeit",
          text: "Vollverschweißte Metallkonstruktionen eignen sich für In-situ-Gasrückblaszyklen, chemische Spülungen und Ultraschallbäder.",
        },
      ],
    },
    related: {
      heading: "Verwandte Anwendungen & Produkte",
      intro: "Entdecken Sie ergänzende Lösungen für die Öl-, Kraftstoff- und Chemieaufbereitung von MutualFilter.",
      cards: [
        {
          title: "Öl-, Kraftstoff- und Petrochemiefiltration",
          text: "Umfassende Ganzmetall-Filtermedien für Hochdruck-Kraftstoffleitungen, Hydraulik und Raffinerieprozesse.",
          to: "/OilFuelFiltrationPage",
          badge: "Hauptanwendung",
          linkLabel: "Übersicht ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Übersicht über die Öl- und Kraftstofffiltration",
          },
        },
        {
          title: "Öl- und Gastrennung",
          text: "Sandschutz für Bohrlöcher, Tropfenabscheidung in Gaswäschern und Aufbereitung von Lagerstättenwasser.",
          to: "/OilGasSeparationPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Öl- & Gastrennung ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Mehrlagige Filterplatte für Öl- und Gastrennung",
          },
        },
        {
          title: "Kraftstoff- und Hydraulikölfiltration",
          text: "Drahtgewebe und Sinterfilter für Flugturbinen, Hydrauliksysteme und Schwerölaufbereitung.",
          to: "/OilFuelFiltrationApplicationsPage",
          badge: "Geschwisteranwendung",
          linkLabel: "Kraftstoff & Hydraulik ansehen",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
            alt: "Filterelement für Kraftstoff- und Hydraulikfiltration",
          },
        },
        {
          title: "Mehrlagiges Sintergewebe",
          text: "Diffusionsgebundene Sinterlaminate mit formstabiler Porengeometrie und hoher Differenzdruckfestigkeit.",
          to: "/SinteredWireMeshPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Sintergewebe entdecken",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Mehrlagige Sinterscheibe für Hydraulik- und Ölfiltration",
          },
        },
        {
          title: "Filterkomponenten",
          text: "Vollverschweißte zylindrische und plissierte Filterkerzen aus Edelstahl für Hochdruckanwendungen.",
          to: "/FilterComponentsPage",
          badge: "Empfohlenes Produkt",
          linkLabel: "Komponenten entdecken",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Plissierte und zylindrische Edelstahl-Filterkerzen",
          },
        },
      ],
    },
    cta: {
      id: "petrochemical-quote",
      eyebrow: "Technische Anfrage",
      heading: "Angebot für petrochemische Filtermedien anfordern",
      lead: "Nennen Sie Filterfeinheit, Prozesschemie, Betriebsbedingungen und Abmessungen für eine technische Auslegung.",
      secondaryLabel: "Zurück zu Öl & Kraftstoff",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Werksprüfzeugnisse (MTR) und PMI-Analysen auf Anfrage gemäß Auftragsvereinbarung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "PetrochemicalProcessFiltration",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Filtration des Huiles, Carburants et Pétrochimie",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toiles et Éléments Frittés en Inox pour Procédés Pétrochimiques",
      lead: "Toiles métalliques tissées et stratifiés multicouches frittés par diffusion pour protéger les lits catalytiques, compresseurs de reformage et l'instrumentation dans des fluides sévères et corrosifs.",
      secondaryCta: {
        label: "Filtration des Huiles et Carburants",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Cuve de filtration industrielle haute pression installée dans une unité pétrochimique",
      },
      cardNote: "Conception tout métal et haute résistance à l'écrasement",
      cardLink: {
        label: "Consultation Technique",
        to: "/ContactPage",
      },
      specs: [
        { label: "Seuil de Filtration", value: "1 µm – 250 µm" },
        { label: "Nuances d'Alliages", value: "AISI 316L / Inconel / C-276" },
        { label: "Médias Filtrants", value: "Stratifié Fritté / Toile Tissée" },
        { label: "Conditions de Service", value: "Service Chimique et Thermique Sévère" },
      ],
    },
    overview: {
      eyebrow: "Génie des Procédés",
      heading: "Filtration en Service Sévère pour Raffinage Catalytique et Synthèse Chimique",
      paragraphs: [
        "Les unités de raffinage pétrochimique, d'hydrocraquage, de reformage catalytique et de synthèse de polymères fonctionnent sous des conditions exigeantes où les flux entraînent des fines de catalyseur, des composés corrosifs et des gaz acides. Dans ces circuits à haute température et forte pression différentielle, les toiles polymères ou fibreuses sont inadaptées.",
        "MutualFilter propose des médias de filtration entièrement métalliques, notamment des stratifiés frittés multicouches et des toiles tissées calandrées lourdes. La matrice rigide conserve une ouverture fixe lors des variations rapides de débit, évitant l'entraînement de particules vers les compresseurs et les échangeurs.",
        "Fabriqués en nuances résistantes telles que l'inox 316L passivé, l'Inconel 625 et le Hastelloy C-276, les éléments résistent aux gaz amers humides, aux chlorures et aux lavages caustiques sans perte d'intégrité.",
      ],
      keyPoints: [
        "Structures poreuses rigides soudées par diffusion préservant les pores microniques sous des pressions différentielles élevées",
        "Stabilité thermique maintenue lors des cycles de réaction et de régénération sans déformation ni relargage",
        "Sélection d'alliages 316L, Inconel 625 et Hastelloy C-276 assurant une tenue exemplaire face aux gaz acides et aux chlorures",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Cuve de filtration industrielle dans une unité pétrochimique",
      },
    },
    specTable: {
      heading: "Configurations de Médias Filtrants pour Procédés Pétrochimiques",
      intro: "Médias métalliques types pour rétention de catalyseurs, protection de compresseurs, traitement des amines et supports de lit.",
      columns: [
        { key: "process", label: "Application de Procédé", align: "left" },
        { key: "construction", label: "Construction du Média", align: "left" },
        { key: "rating", label: "Seuil de Filtration", align: "right" },
        { key: "alloys", label: "Alliages Standards", align: "right" },
        { key: "cleaning", label: "Mode de Régénération", align: "right" },
      ],
      rows: [
        {
          process: "Rétention de Catalyseur d'Hydrocraquage",
          construction: "Stratifié fritté multicouche 5 couches",
          rating: "2 µm – 40 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Rétrosoufflage gaz / ultrasons",
        },
        {
          process: "Protection Compresseur de Reformage",
          construction: "Cartouche plissée en tissage hollandais sergé",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L / Hastelloy C-276",
          cleaning: "Lavage solvant / contre-lavage",
        },
        {
          process: "Boucles d'Amines & Rebouilleurs de Glycol",
          construction: "Cylindre poreux en stratifié fritté",
          rating: "5 µm – 75 µm",
          alloys: "AISI 316L",
          cleaning: "Purge vapeur / circulation solvant",
        },
        {
          process: "Support de Lit de Réacteur",
          construction: "Panneau plan à fil profilé en V",
          rating: "100 µm – 250 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Lavage haute pression / raclage",
        },
      ],
      caption: "Toutes les dimensions, fentes et nuances d'acier sont réalisées sur mesure selon les plans de cuves et contraintes d'exploitation.",
    },
    features: {
      heading: "Atouts Fonctionnels et de Fabrication",
      intro: "Nos médias métalliques inox et nickel combinent résistance mécanique à l'écrasement, stabilité thermique et précision de coupure constante.",
      items: [
        {
          title: "Zéro Relargage de Fibres",
          text: "Le frittage par diffusion soude les fils à chaque intersection, supprimant tout risque de migration de matière dans les flux chimiques nobles.",
        },
        {
          title: "Rigidité Mécanique et Thermique",
          text: "Des toiles de soutien lourdes et des tubes perforés internes absorbent les à-coups de pression et les contraintes cycliques sans déformation.",
        },
        {
          title: "Résistance aux Environnements Sévères",
          text: "L'emploi d'inox 316L passivé et d'Inconel 625 protège contre la corrosion sous contrainte et le piquage en présence de soufre et de chlorures.",
        },
        {
          title: "Régénération Fiable",
          text: "Assemblages soudés conçus pour supporter les impulsions de décolmatage gazeux, les lavages solvants et les bacs à ultrasons.",
        },
      ],
    },
    related: {
      heading: "Applications et Produits Associés",
      intro: "Découvrez nos solutions complémentaires en séparation pour hydrocarbures et médias métalliques de précision.",
      cards: [
        {
          title: "Filtration des Huiles et Carburants",
          text: "Solutions métalliques complètes pour circuits hydrauliques haute pression, lubrification et raffinage.",
          to: "/OilFuelFiltrationPage",
          badge: "Application Principale",
          linkLabel: "Voir la Présentation",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Présentation filtration huiles et carburants",
          },
        },
        {
          title: "Séparation Pétrole et Gaz",
          text: "Contrôle du sable en fond de puits, dévésiculeurs pour gaz et clarification des eaux de production.",
          to: "/OilGasSeparationPage",
          badge: "Application Associée",
          linkLabel: "Explorer Pétrole & Gaz",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Panneau filtrant pour séparation pétrole et gaz",
          },
        },
        {
          title: "Applications Carburants et Fluides Hydrauliques",
          text: "Médias tissés et frittés pour kérosène d'aviation, servocommandes hydrauliques et fioul lourd.",
          to: "/OilFuelFiltrationApplicationsPage",
          badge: "Application Associée",
          linkLabel: "Explorer Carburants & Hydraulique",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
            alt: "Élément filtrant pour carburants et fluides hydrauliques",
          },
        },
        {
          title: "Toile Frittée Multicouche",
          text: "Stratifiés frittés par diffusion assurant géométrie rigide, haute résistance mécanique et zéro relargage.",
          to: "/SinteredWireMeshPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer la Toile Frittée",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disque laminé fritté multicouche pour filtration industrielle",
          },
        },
        {
          title: "Composants Filtrants",
          text: "Cartouches cylindriques et plissées entièrement soudées en acier inoxydable pour circuits haute pression.",
          to: "/FilterComponentsPage",
          badge: "Produit Recommandé",
          linkLabel: "Explorer les Composants",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Cartouches filtrantes inox plissées et cylindriques",
          },
        },
      ],
    },
    cta: {
      id: "petrochemical-quote",
      eyebrow: "Étude Technique",
      heading: "Demandez un Devis pour Filtres de Procédés Pétrochimiques",
      lead: "Précisez votre seuil de filtration, la nature chimique du fluide, les conditions thermiques et l'encombrement requis.",
      secondaryLabel: "Retour aux Huiles & Carburants",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Certificats matière (MTR) et contrôles PMI disponibles sur demande selon les spécifications de la commande.",
    },
  },
  pt: {
    breadcrumbKey: "PetrochemicalProcessFiltration",
    relatedTopic: "oil",
    hero: {
      categoryBadge: "Filtração de Óleo, Combustível e Petroquímica",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Telas e Elementos Sinterizados em Aço Inox para Processos Petroquímicos",
      lead: "Telas de arame tecidas e laminados multicamadas sinterizados por difusão projetados para proteger leitos de catalisadores, compressores de reforma e instrumentação em fluidos agressivos e corrosivos.",
      secondaryCta: {
        label: "Filtração de Óleo e Combustível",
        to: "/OilFuelFiltrationPage",
      },
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Vaso de filtração industrial de alta pressão em unidade petroquímica",
      },
      cardNote: "Construção toda metálica e alta resistência ao colapso",
      cardLink: {
        label: "Consulta Técnica",
        to: "/ContactPage",
      },
      specs: [
        { label: "Grau de Filtração", value: "1 µm – 250 µm" },
        { label: "Ligas Padrão", value: "AISI 316L / Inconel / C-276" },
        { label: "Meios Filtrantes", value: "Laminado Sinterizado / Tela Tecida" },
        { label: "Ambiente Operacional", value: "Serviço Químico e Térmico Severo" },
      ],
    },
    overview: {
      eyebrow: "Engenharia de Processos",
      heading: "Filtração em Serviço Severo para Refino Catalítico e Síntese Química",
      paragraphs: [
        "Unidades de refino petroquímico, hidrocraqueamento, reforma catalítica e síntese química operam continuamente sob condições extremas com arraste de particulados finos de catalisadores, espécies químicas ácidas e gases corrosivos. Nestes circuitos de alta temperatura e alta pressão diferencial, meios poliméricos ou fibrosos sofrem degradação precoce.",
        "A MutualFilter fabrica meios de filtração inteiramente metálicos, incluindo laminados sinterizados multicamadas unidos por difusão e telas tecidas calandradas pesadas. A estrutura porosa rígida mantém a geometria micrométrica inalterada durante pulsos de fluxo e transientes de pressão, protegendo compressores, válvulas e permutadores térmicos.",
        "Produzidos em ligas de alta resistência como AISI 316L passivado, Inconel 625 e Hastelloy C-276, os elementos suportam atmosferas amargas com H2S, cloretos e regenerações cáusticas periódicas sem perda de rigidez mecânica.",
      ],
      keyPoints: [
        "Compósitos porosos rígidos por difusão que mantêm os poros micrométricos sob elevadas pressões diferenciais",
        "Estabilidade térmica em ciclos contínuos de reação e regeneração sem distorção das aberturas nem desprendimento",
        "Opções em 316L, Inconel 625 e Hastelloy C-276 resistentes a pites e trincas sob tensão em presença de H2S e cloretos",
      ],
      image: {
        src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-petrochemical-6c7ebe.png",
        alt: "Vaso de filtração industrial de alta pressão em refinaria petroquímica",
      },
    },
    specTable: {
      heading: "Configurações de Meios Filtrantes para Processos Petroquímicos",
      intro: "Meios metálicos representativos para retenção de catalisadores, lavagem de gás, circuitos de amina e suporte de leito.",
      columns: [
        { key: "process", label: "Aplicação do Processo", align: "left" },
        { key: "construction", label: "Construção do Meio Filtrante", align: "left" },
        { key: "rating", label: "Grau de Filtração", align: "right" },
        { key: "alloys", label: "Ligas Metálicas", align: "right" },
        { key: "cleaning", label: "Método de Regeneração", align: "right" },
      ],
      rows: [
        {
          process: "Retenção de Catalisador em Hidrocraqueamento",
          construction: "Compósito de tela sinterizada de 5 camadas",
          rating: "2 µm – 40 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Retrosopro por gás / ultrassom",
        },
        {
          process: "Proteção de Compressores de Reforma",
          construction: "Cartucho plissado em trama holandesa sarja",
          rating: "1 µm – 25 µm",
          alloys: "AISI 316L / Hastelloy C-276",
          cleaning: "Lavagem com solvente / retrolavagem",
        },
        {
          process: "Tratamento de Aminas & Reboiledor de Glicol",
          construction: "Cilindro poroso em laminado sinterizado",
          rating: "5 µm – 75 µm",
          alloys: "AISI 316L",
          cleaning: "Purga a vapor / circulação de solvente",
        },
        {
          process: "Suporte de Leito em Reatores",
          construction: "Painel plano de arame em cunha contínuo",
          rating: "100 µm – 250 µm",
          alloys: "AISI 316L / Inconel 625",
          cleaning: "Jato de alta pressão / limpeza mecânica",
        },
      ],
      caption: "Todas as dimensões, aberturas micrométricas e conexões são customizadas conforme os projetos de engenharia do cliente.",
    },
    features: {
      heading: "Destaques Construtivos e Operacionais",
      intro: "Nossos meios filtrantes em inox e ligas de níquel unem alta resistência mecânica ao colapso, estabilidade térmica e uniformidade geométrica.",
      items: [
        {
          title: "Zero Migração de Partículas",
          text: "A sinterização por difusão funde as interseções dos fios, eliminando riscos de desprendimento ou contaminação em correntes químicas de alto valor.",
        },
        {
          title: "Rigidez Mecânica e Estrutural",
          text: "Malhas de reforço espessas e tubos internos perfurados toleram choques hidráulicos severos e amplas variações térmicas sem empenamento.",
        },
        {
          title: "Compatibilidade Química Extrema",
          text: "Construção em AISI 316L passivado, Inconel 625 e Hastelloy C-276 resistente a corrosão sob tensão e pites por cloretos e H2S.",
        },
        {
          title: "Regenerabilidade Repetível",
          text: "Estruturas totalmente soldadas aptas a ciclos de retrossopro com gás, lavagens químicas com solventes e banhos de ultrassom.",
        },
      ],
    },
    related: {
      heading: "Aplicações e Produtos Relacionados",
      intro: "Conheça soluções integradas em filtração de hidrocarbonetos e meios metálicos desenvolvidos pela MutualFilter.",
      cards: [
        {
          title: "Filtração de Óleo e Combustível",
          text: "Soluções completas de separação para linhas hidráulicas de alta pressão, lubrificação e refino.",
          to: "/OilFuelFiltrationPage",
          badge: "Aplicação Principal",
          linkLabel: "Ver Visão Geral",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-d96266.png",
            alt: "Visão geral de filtração de óleo e combustível",
          },
        },
        {
          title: "Separação de Óleo e Gás",
          text: "Controle de areia em poços, eliminação de névoa em gás e tratamento de água produzida.",
          to: "/OilGasSeparationPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Óleo e Gás",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-81fe57.png",
            alt: "Painel de malha multicamada para separação de óleo e gás",
          },
        },
        {
          title: "Aplicações em Combustíveis e Fluidos Hidráulicos",
          text: "Telas tecidas e sinterizadas para querosene de aviação, circuitos de potência e óleo combustível pesado.",
          to: "/OilFuelFiltrationApplicationsPage",
          badge: "Aplicação Irmã",
          linkLabel: "Explorar Combustíveis & Hidráulica",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-sintered-st-8d5c3f.png",
            alt: "Elemento filtrante para combustível e fluidos hidráulicos",
          },
        },
        {
          title: "Malha Sinterizada Multicamadas",
          text: "Laminados sinterizados por difusão oferecendo estrutura rígida de poros e alta resistência ao colapso.",
          to: "/SinteredWireMeshPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Malha Sinterizada",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/sintered-mesh-5layer-disc.webp",
            alt: "Disco laminado sinterizado multicamadas para filtração de óleo e combustível",
          },
        },
        {
          title: "Componentes Filtrantes",
          text: "Cartuchos cilíndricos e plissados totalmente soldados em aço inoxidável para circuitos de alta pressão.",
          to: "/FilterComponentsPage",
          badge: "Produto Recomendado",
          linkLabel: "Explorar Componentes",
          image: {
            src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-a-stainless-s-001099.png",
            alt: "Cartuchos filtrantes plissados e cilíndricos em aço inoxidável",
          },
        },
      ],
    },
    cta: {
      id: "petrochemical-quote",
      eyebrow: "Consulta de Engenharia",
      heading: "Solicite uma Cotação para Filtros de Processos Petroquímicos",
      lead: "Informe o grau de retenção micrométrico, química do fluido, condições operacionais e dimensões para avaliação de engenharia.",
      secondaryLabel: "Voltar para Óleo e Combustível",
      secondaryTo: "/OilFuelFiltrationPage",
      note: "Relatórios de ensaio de material (MTR) e análise PMI disponíveis quando especificados no pedido.",
    },
  },
};
